import { afterNextRender, Component, ElementRef, inject, Injector, model, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { IPluginComponent, REPORT_TRIGGER_EXECUTION } from '@phx-erp/shared-ui';
import { Apollo } from 'apollo-angular';
import { filter } from 'rxjs/operators';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { notifyPluginAppsOfBrowserUrl } from '../../notify-plugin-apps-of-browser-url';

export interface CustomElementHostComponentProps {
  tagName: string;
  scriptPath: string;
  manifestSource: string;
}

export function getScriptPath(scriptPath: string) {
  if (scriptPath.startsWith('/assets/custom/elements'))
    return scriptPath;
  if (scriptPath.startsWith('assets/custom/elements'))
    return "/" + scriptPath;
  return !scriptPath ? undefined : scriptPath.includes(':') ? scriptPath : `/assets/custom/elements${scriptPath}`;
}

@Component({
  selector: 'app-custom-element-host',
  imports: [],
  template: `<div #host class="block h-full w-full"></div>`,
})
export class CustomElementHostComponent {
  props = model<CustomElementHostComponentProps>();
  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');
  private readonly router = inject(Router);
  private pluginElementMounted = false;

  constructor(
    apollo: Apollo,
    private readonly injector: Injector,
  ) {
    afterNextRender(async () => {
      // Resolve after render so we do not participate in a synchronous DI cycle (NG0200)
      // while BaseListCombinedService or its transitive deps are still hydrating.
      const baseListCombinedService = this.injector.get(BaseListCombinedService);
      const reportTriggerExecution = this.injector.get(REPORT_TRIGGER_EXECUTION);

      let moduleUrl = this.props().scriptPath;
      if (!moduleUrl) {
        const manifest = await fetch(this.props().manifestSource).then((r) => r.json());
        moduleUrl = manifest.path;
      }
      await import(/* @vite-ignore */getScriptPath(moduleUrl));
      await customElements.whenDefined(this.props().tagName);
      const root = this.host().nativeElement;
      root.replaceChildren();
      const el = document.createElement(this.props().tagName) as IPluginComponent & HTMLElement;
      el.hostInjector = this.injector;
      el.pluginServices = {
        apollo: apollo,
        baseListCombinedService,
        reportTriggerExecution,
      };
      root.appendChild(el);
      this.pluginElementMounted = true;
      // Plugin ApplicationRef/Router is a singleton; remount must re-sync to the current URL.
      notifyPluginAppsOfBrowserUrl();
    });

    // Same mount (`/customElements/:route/**`): host page stays alive, only the child URL changes.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.pluginElementMounted) {
          notifyPluginAppsOfBrowserUrl();
        }
      });
  }
}
